// MessagePanelBottom.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import NeedAuth from '../container/NeedAuth.js';
import MessageMeta from '../container/MessageMeta.js';
import MessageInput from '../container/MessageReplyInput.js';
import UnlockButton from '../component/UnlockButton.jsx';

import ChatBtnSrc from '../../img/btn_chat_24.svg';
import GiftIcon from '../../img/ic_gift_fill.svg';

import trackMixpanel, {
  getViewId,
  ButtonId,
  EventTypes,
} from '../resource/mixpanel.js';
import withNotificationPaddingMonitor from '../resource/withNotificationPaddingMonitor.js';
import { DrmFeatureName } from '../resource/drmConstants.js';
import { MessageCtaType } from '../resource/messageConstants.js';
import media from '../style/media.js';

export class MessagePanelBottom extends React.PureComponent {
  componentDidMount() {
    const { userId, subscribeUserReplyPrice } = this.props;
    subscribeUserReplyPrice({ userId });
  }

  componentDidUpdate(prevProps) {
    const { userId: prevUserId } = prevProps;
    const { userId, subscribeUserReplyPrice } = this.props;
    if (userId !== prevUserId) {
      subscribeUserReplyPrice({ userId });
    }
  }

  handleGiftClick = () => {
    const { userId, isAuthed, login, onGiftClick } = this.props;
    if (isAuthed) {
      onGiftClick(userId);
    } else {
      login();
    }
  };

  handleChatAudioClick = () => {
    const { userId, isAuthed, login, sendChatStart } = this.props;
    if (isAuthed) {
      sendChatStart(userId);
    } else {
      login();
    }
  };

  trackUserClick = buttonId => {
    const { id } = this.props;
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
        messageId: id,
      },
    });
  };

  handleClickChat = () => {
    const { userId, sendChatStart } = this.props;

    this.trackUserClick(ButtonId.All.ButtonChat);
    return sendChatStart(userId);
  };

  handleUnlockButtonClick = event => {
    const {
      id,
      type,
      unlockButtonStatus,
      isAuthed,
      ctaUrlData,
      login,
      sendUnlockMessage,
    } = this.props;
    const drmFeatureName = ['chat', 'reply'].includes(type)
      ? DrmFeatureName.CHAT
      : DrmFeatureName.STORY;
    if (unlockButtonStatus === 'loading') {
      return event.stopPropagation();
    } else if (!isAuthed) {
      return login();
    } else {
      return sendUnlockMessage({
        id,
        shouldCheckDrm: true,
        shouldOpenMessagePackUpsellingModal:
          MessageCtaType.PRODUCT === ctaUrlData.type,
        drmFeatureName,
      });
    }
  };

  renderUnlockButton() {
    const { isMe, isAuthed, unlockPrice, showUnlockButton } = this.props;

    if (!showUnlockButton || isMe) {
      return null;
    }

    return (
      <OperationRow>
        {isAuthed && (
          <ChatBtnWrapper onClick={this.handleClickChat}>
            <ChatBtn>
              <img src={ChatBtnSrc} />
            </ChatBtn>
          </ChatBtnWrapper>
        )}
        <UnlockWrapper>
          <UnlockButton
            data-element_id={
              isAuthed
                ? ButtonId.All.ButtonUnlock
                : ButtonId.Detail.ButtonDetailSignup
            }
            status={this.props.unlockButtonStatus}
            price={unlockPrice}
            onClick={event => {
              this.trackUserClick(
                isAuthed
                  ? ButtonId.All.ButtonUnlock
                  : ButtonId.Detail.ButtonDetailSignup
              );
              this.handleUnlockButtonClick(event);
            }}
          />
        </UnlockWrapper>
        {isAuthed && (
          <GiftBtnWrapper
            onClick={() => {
              this.trackUserClick(ButtonId.All.ButtonGift);
              this.handleGiftClick();
            }}
          >
            <GiftBtn>
              <img src={GiftIcon} />
            </GiftBtn>
          </GiftBtnWrapper>
        )}
      </OperationRow>
    );
  }

  renderInput() {
    const { id, userId, replyPrice, isMe, isSending, showInput, setExpanded } =
      this.props;
    if (!showInput || isMe) {
      return null;
    }

    return (
      <NeedAuth>
        <OperationRow data-key="needAuth">
          <UnlockWrapper>
            <UnlockButton
              status="unauthorized"
              i18nKey="message_detail_login"
              onClick={event => {
                this.trackUserClick(ButtonId.Detail.ButtonFreeDetailSignup);
                this.handleUnlockButtonClick(event);
              }}
            />
          </UnlockWrapper>
        </OperationRow>

        <div data-key="authing" />
        <MessageInputContainer data-key="authed">
          <MessageInputWrapper>
            <MessageInput
              showAudio={false}
              userId={userId}
              isLoading={isSending}
              messageId={id}
              cost={replyPrice}
              onFocus={() =>
                setExpanded({ expandedField: 'input', messageId: id })
              }
              onBlur={() => setExpanded({ expandedField: null, messageId: id })}
              directToChatGift={this.handleGiftClick}
              directToChatByAudio={this.handleChatAudioClick}
            />
          </MessageInputWrapper>
        </MessageInputContainer>
      </NeedAuth>
    );
  }

  render() {
    const {
      id,
      showMeta,
      isFree,
      isUnlocked,
      isFreeZone,
      showUnlockButton,
      showInput,
    } = this.props;

    const showOperation = showInput || showUnlockButton;

    return (
      <StyledMessagePanelBottom>
        {showMeta && (
          <MetaRow isBottom={!showOperation}>
            <MessageMeta
              id={id}
              showExpires={(!isUnlocked || isFree) && !isFreeZone}
              showUnlocks={!isFree && !isFreeZone}
              showLikes={!isFree && !isFreeZone}
            />
          </MetaRow>
        )}
        {this.renderUnlockButton()}
        {this.renderInput()}
      </StyledMessagePanelBottom>
    );
  }
}

MessagePanelBottom.propTypes = {
  id: PropTypes.string,
  isMe: PropTypes.bool,
  userId: PropTypes.string,
  type: PropTypes.string,
  replyPrice: PropTypes.number,
  isAuthed: PropTypes.bool,
  isSending: PropTypes.bool,
  isFree: PropTypes.bool,
  isUnlocked: PropTypes.bool,
  isFreeZone: PropTypes.bool,
  showMeta: PropTypes.bool,
  showUnlockButton: PropTypes.bool,
  showInput: PropTypes.bool,
  unlockButtonStatus: PropTypes.string,
  unlockPrice: PropTypes.number,
  ctaUrlData: PropTypes.object,
  sendChatStart: PropTypes.func,
  onGiftClick: PropTypes.func,
  login: PropTypes.func,
  sendUnlockMessage: PropTypes.func,
  setExpanded: PropTypes.func,
  subscribeUserReplyPrice: PropTypes.func,
};

MessagePanelBottom.defaultProps = {
  id: '',
  isMe: false,
  userId: '',
  type: null,
  replyPrice: null,
  isAuthed: false,
  isSending: false,
  isFree: false,
  isUnlocked: false,
  isFreeZone: false,
  showMeta: false,
  showUnlockButton: false,
  showInput: false,
  unlockPrice: 0,
  unlockButtonStatus: '',
  ctaUrlData: {},
  sendChatStart: () => null,
  onGiftClick: () => null,
  login: () => null,
  setExpanded: () => null,
  sendUnlockMessage: () => null,
  subscribeUserReplyPrice: () => null,
};

const StyledMessagePanelBottom = styled.div`
  pointer-events: auto;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px 0;
  margin-bottom: ${({ isBottom }) => (isBottom ? 24 : 22)}px;
  ${media.mobile`
    margin-bottom: ${({ isBottom }) => (isBottom ? 24 : 16)}px;
  `}
`;

const OperationRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 16px 20px 16px;
  ${media.mobile`
    padding: 0 16px 16px 16px;
  `}
`;

const MessageInputContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  padding: 0 16px 20px 16px;
  ${media.mobile`
    padding: 0 16px 16px 16px;
  `}
`;

const MessageInputWrapper = styled.div`
  flex: auto;
  width: 100%;
`;

const ChatBtnWrapper = styled.button`
  flex: none;
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-image: linear-gradient(
    136deg,
    #00d2be 14%,
    #5d82bc 52%,
    #8e37d7 86%
  );
  outline: none;
  border: 0;
  margin-right: 16px;
  ${media.mobile`
    margin-right: 12px;
  `}
`;
const ChatBtn = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  img {
    width: 100%;
    height: 100%;
  }
`;

const UnlockWrapper = styled.div`
  flex: auto;
  width: 100%;
`;

const GiftBtnWrapper = styled.button`
  flex: none;
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-image: linear-gradient(
    136deg,
    #00d2be 14%,
    #5d82bc 52%,
    #8e37d7 86%
  );
  outline: none;
  border: 0;
  margin-left: 12px;
`;
const GiftBtn = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  img {
    width: 100%;
    height: 100%;
  }
`;

export default withTranslation()(
  withNotificationPaddingMonitor(MessagePanelBottom)
);
